<template>
  <div class="io-hero-wrapper">
    <div
        ref="root"
        :class="heroClasses"
    >
      <BaseGridRow
          class="position-relative"
          :min-height="minHeight"
          no-gap
          :vertical-center="contentAlign === 'center'"
          :vertical-end="contentAlign === 'end'"
      >
        <BaseGridCol>
          <div class="io-hero__container">
            <div
                class="container-fluid"
                ref="heroContainer"
            >
              <div class="io-hero__container__content">
                <div class="io-hero__headline-row">
                  <div class="io-hero__headline-row__icon">
                    <LazyBackgroundMedia
                        v-if="hasIcon"
                        :key="`hero-media-${currentIconMedia.id}`"
                        :media-id="currentIconMedia.id"
                        :media-type="currentIconMedia.type"
                        :media-url="currentIconMedia.url"
                        :background-position-y="currentIconMedia.positionY"
                        :background-position-x="currentIconMedia.positionX"
                        immediate
                    />
                  </div>
                  <div>
                    <IoHeadline
                        size="h1"
                        text-align="start"
                        :color="fontColor"
                    >
                      <slot name="headline"></slot>
                    </IoHeadline>
                  </div>
                </div>
                <IoHeadline
                    v-if="hasSubline"
                    size="h1"
                    tag-name="h2"
                    text-align="start"
                    :color="fontColor"
                >
                  <slot name="subline"></slot>
                </IoHeadline>
                <div
                    v-if="hasContent"
                    class="io-hero__content"
                    :class="`color-${fontColor}`"
                >
                  <slot name="content"></slot>
                </div>
                <BaseButton
                    v-if="pageUrl"
                    :page-url="pageUrl"
                    :color="fontColor"
                    link-type="page"
                    type="outline"
                    align="start"
                >
                  <span v-html="pageUrlLabel"></span>
                </BaseButton>
                <BaseCalculator
                    ref="calculatorComponent"
                    v-if="calculatorShow === 'true'"
                    :label-extra="calculatorLabelExtra"
                    :branches="calculatorBranches"
                    :show-valid-checks="true"
                    :is-business-active="isBusinessActive"
                />
              </div>
            </div>
          </div>
        </BaseGridCol>
      </BaseGridRow>
      <div
          class="io-hero__background"
      >
        <LazyBackgroundMedia
            ref="background"
            class="io-hero__background-media"
            :media-id="mediaId"
            :media-url="mediaUrl"
            :media-id-desktop="mediaIdDesktop"
            :media-url-desktop="mediaUrlDesktop"
            :background-position-y="backgroundPositionY"
            :background-position-x="backgroundPositionX"
            :background-position-y-desktop="backgroundPositionYDesktop"
            :background-position-x-desktop="backgroundPositionXDesktop"
            :preview-media-url="currentPreviewMedia.url"
            immediate
        />
        <div
            class="io-hero__background-overlay fadeout"
            :class="{
              [`app-bg-${colorGradient}`]: true,
              'to-bottom': isMobileBreakpoint
            }"
        />
      </div>
    </div>
    <div
        v-if="hasDisruptor"
        class="io-hero__disruptor"
        :class="{
          'is-mobile': isMobileBreakpoint,
          [`app-bg--${disruptorBackground}`]: true,
        }"
        :style="disruptorStyles"
    >
      <Observer
          ref="disruptorWaveObserver"
          @resize="onDisruptorWaveResize"
      >
        <IoWave
            ref="disruptorWave"
            v-show="isMobileBreakpoint"
            class="disruptor-wave"
            :mask-id="disruptorWaveId"
            wave-type="wave"
            :wave-mirrored="true"
            :wave-negative="true"
        />
      </Observer>
      <div class="container-fluid">
        <div class="position-relative">
          <div
              ref="disruptorWrapper"
              class="io-hero__disruptor__content-wrapper"
              :class="`app-bg-${disruptorBackground} gradient`"
          >
            <div class="io-hero__disruptor__content">
              <slot name="disruptor"></slot>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import BaseButton from '@/components/BaseButton.vue';
import IoWave from '@/components/IoWave.vue';
import Observer from '@/components/Observer.vue';
import { HeroPropsDefaults, HeroPropsInterface } from '@/data/Hero';
import { getRandomId } from '@/helpers';
import {
  reactive,
  computed,
  onMounted,
  onBeforeUnmount,
  ref,
  watch,
  nextTick,
  useSlots, inject, Ref,
} from 'vue';

const props = withDefaults(defineProps<HeroPropsInterface>(), HeroPropsDefaults);

const slots = useSlots();
const header = reactive({
  $el: null as null | HTMLElement,
});
const heroContainer = ref<InstanceType<typeof HTMLElement>>();
const root = ref<InstanceType<typeof HTMLElement>>();
const background = ref<InstanceType<typeof LazyBackgroundMedia>>();
const calculatorComponent = ref();
const isMobileBreakpoint = inject('isMobileBreakpoint') as Ref<boolean>;
const currentPreviewMedia = computed<Record<symbol, unknown>>(() => ({
  id: isMobileBreakpoint.value ? props.previewMediaId : (props.previewMediaIdDesktop || props.previewMediaId),
  type: isMobileBreakpoint.value ? props.previewMediaType : (props.previewMediaTypeDesktop || props.previewMediaType),
  sizes: isMobileBreakpoint.value ? props.previewMediaSizes : (props.previewMediaSizesDesktop || props.previewMediaSizes),
  alt: isMobileBreakpoint.value ? props.previewMediaAlt : (props.previewMediaAltDesktop || props.previewMediaAlt),
  url: isMobileBreakpoint.value ? props.previewMediaUrl : (props.previewMediaUrlDesktop || props.previewMediaUrl),
}));
const currentIconMedia = computed<Record<symbol, unknown>>(() => ({
  id: props.iconMediaId,
  type: props.iconMediaType,
  sizes: props.iconMediaSizes,
  alt: props.iconMediaAlt,
  url: props.iconMediaUrl,
  positionX: 'center',
  positionY: 'center',
}));
const hasIcon = computed<boolean>(() => ![undefined, null, 0, '0', ''].includes(currentIconMedia.value.id || 0));

const subline = slots.subline ? slots.subline() : undefined;
const hasSubline = computed(() => (
  subline && subline[0] ? `${subline[0].children}`.trim().length > 0 : false
));
const content = slots.content ? slots.content() : undefined;
const hasContent = computed(() => (
  content && content[0] ? `${content[0].children}`.trim().length > 0 : false
));

const disruptor = slots.disruptor ? slots.disruptor() : undefined;
const hasDisruptor = computed(() => (
  disruptor && disruptor[0] ? `${disruptor[0].children}`.trim().length > 0 : false
));

const minHeight = ref<number | null>(null);

const setMinHeight = () => {
  minHeight.value = null;
  let headerHeight = 0;

  if (header.$el) {
    headerHeight = header.$el.offsetHeight;
  }

  switch (props.height) {
    case 'full':
      minHeight.value = window.innerHeight - headerHeight;
      break;
    case 'small':
      minHeight.value = (window.innerHeight - headerHeight) * 0.537037;
      break;
    case 'auto':
      minHeight.value = 0;
      break;
    default:
      minHeight.value = (window.innerHeight - headerHeight) * 0.7407407;
  }
};

const resizeHandler = () => {
  setMinHeight();
};

const heroClasses = [
  'io-hero',
  `text-${props.fontColor}`,
];

const disruptorWave = ref<InstanceType<typeof IoWave>>();
const disruptorWaveObserver = ref<InstanceType<typeof Observer>>();
const disruptorWrapper = ref<HTMLElement>();
const disruptorSvgMaskUrl = `${window.ioappConfig.themeUri}/public/img/wave/WelleNegativGespiegelt.svg`;
const disruptorWaveId = getRandomId('wave-');
const disruptorMaskUrl = `url(#${disruptorWaveId})`;
const disruptorMaskSvgUrl = `url('${disruptorSvgMaskUrl}')`;
const disruptorStyles = ref<Record<string, string>>({});

const onDisruptorWaveResize = ({ height }: Record<string, number | undefined>) => {
  disruptorStyles.value.marginTop = '0px';

  if (!isMobileBreakpoint.value) return;

  disruptorStyles.value.marginTop = `-${height || 0}px`;
};
const resizeDisruptor = () => {
  disruptorStyles.value.marginTop = '0px';

  if (!disruptorWrapper.value) return;

  if (isMobileBreakpoint.value) {
    disruptorWrapper.value.style.removeProperty('height');
    disruptorWrapper.value.style.removeProperty('width');
    disruptorWrapper.value.style.removeProperty('height');
    disruptorWrapper.value.style.removeProperty('--app-disruptor-padding-x');
    disruptorWrapper.value.style.removeProperty('--app-disruptor-padding-y');

    disruptorWaveObserver.value?.setRootSize();

    return;
  }

  let bestWidthFound = false;
  let widthToTest = 180;
  let lastDiff = 9999999999999;
  let lastWidth = widthToTest;
  let lastHeight = 0;

  while (!bestWidthFound) {
    disruptorWrapper.value.style.setProperty('height', 'auto');
    disruptorWrapper.value.style.setProperty('width', `${widthToTest}px`);
    disruptorWrapper.value.style.setProperty('--app-disruptor-padding-x', `${widthToTest * 0.10}px`);
    disruptorWrapper.value.style.setProperty('--app-disruptor-padding-y', `${widthToTest * 0.17}px`);

    const curHeight = disruptorWrapper.value.offsetHeight;
    const curDiff = Math.abs(widthToTest - curHeight);

    if (curDiff < lastDiff) {
      lastWidth = widthToTest;
      lastHeight = curHeight;
      widthToTest += 20;
      lastDiff = curDiff;
    } else {
      const sizeToUse = lastWidth > lastHeight ? lastWidth : lastHeight;
      disruptorWrapper.value.style.setProperty('height', `${sizeToUse}px`);
      disruptorWrapper.value.style.setProperty('width', `${sizeToUse}px`);
      disruptorWrapper.value.style.setProperty('--app-disruptor-padding-x', `${sizeToUse * 0.10}px`);
      disruptorWrapper.value.style.setProperty('--app-disruptor-padding-y', `${sizeToUse * 0.17}px`);
      bestWidthFound = true;
    }
  }
};

onMounted(() => {
  header.$el = document.querySelector('header');
  window.addEventListener('resize', resizeHandler);
  window.addEventListener('load', resizeHandler);
  resizeHandler();

  if (hasDisruptor.value) {
    window.addEventListener('resize', resizeDisruptor);
    window.addEventListener('load', resizeDisruptor);
    resizeDisruptor();
  }

  for (let i = 100; i < 1000; i += 100) {
    setTimeout(resizeHandler, i);

    if (hasDisruptor.value) {
      setTimeout(resizeDisruptor, i);
    }
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', resizeHandler);
  window.removeEventListener('load', resizeHandler);

  if (hasDisruptor.value) {
    window.removeEventListener('resize', resizeDisruptor);
    window.removeEventListener('load', resizeDisruptor);
  }
});

watch(() => calculatorComponent.value, () => {
  nextTick(resizeHandler);
});
</script>

<style lang="scss">
@import "src/scss/component-helpers";

.io-hero {
  position: relative;
  z-index: 3;
  width: 100%;

  &-wrapper {
    position: relative;
    width: 100%;
  }

  &__container {
    position: relative;
    z-index: 2;

    &:before, &:after {
      content: '';
      display: block;
      height: 50px;

      @media (min-width: map_get($grid-breakpoints, 'lg')) {
        height: 70px;
      }
    }

    &__content {
      max-width: 870px;
      width: 100%;
      margin-right: auto;
    }
  }

  &__content {
    @include font-regular(18px);
    max-width: 592px;
  }

  &__video {
    position: fixed;
    top: 0;
  }

  .white:before {
    background: $white-bg-gradient;
  }

  &__background {
    position: absolute !important;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;

    &-media {
      height: 100%;
      width: 100%;

      > div {
        background-size: cover;
        background-position: top center;
        background-repeat: no-repeat;
      }
    }

    &-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 75%;
      z-index: 1;

      @media (min-width: map_get($grid-breakpoints, 'lg')) {
        width: 44.79167%;
        min-width: 360px;
        height: 100%;
      }
    }
  }

  .calculator__form {
    max-width: 646px;
  }

  .io-hero__headline-row {
    width: 100%;
    display: grid;
    grid-template-columns: auto 1fr;

    > div {
      &.io-hero__headline-row__icon {
        grid-column: 1 / 2;
        padding-right: 20px;
        align-self: start;

        @media (min-width: map_get($grid-breakpoints, 'lg')) {
          padding-right: 30px;
        }

        &:empty {
          padding-right: 0 !important;
        }

        > div {
          box-shadow: 0 2px 4px rgba(0, 0, 0, .15);
          background-color: #FFFFFF;
          width: 3.875rem;
          height: 3.875rem;
          border-radius: 10rem;
          position: relative;

          @media (min-width: map_get($grid-breakpoints, 'lg')) {
            width: 6.875rem;
            height: 6.875rem;
          }

          > div {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translateX(-50%) translateY(-50%);
            height: 70.96774%;
            width: 70.96774%;
            background-size: contain;
            background-repeat: no-repeat;

            @media (min-width: map_get($grid-breakpoints, 'lg')) {
              height: 72.72727%;
              width: 72.72727%;
            }
          }
        }
      }

      &:last-child {
        grid-column: 2 / 3;
        align-self: center;
      }
    }

    h1 {
      font-weight: 700;
    }

    ~ div {
      margin-top: 10px;

      @media (min-width: map_get($grid-breakpoints, 'lg')) {
        margin-top: 20px;
      }

      &:empty {
        display: none;
      }
    }

    + h2 {
      font-size: 1.25rem;
      margin-top: 10px;

      @media (min-width: map_get($grid-breakpoints, 'lg')) {
        margin-top: 30px;
        font-size: 1.875rem;
      }
    }

    ~ a {
      margin-top: 2.22222em;
    }

    ~ .calculator, ~ .calculator-hint {
      margin-top: 20px;

      @media (min-width: map_get($grid-breakpoints, 'lg')) {
        margin-top: 40px;
      }
    }
  }

  &__disruptor {
    @media (max-width: #{map_get($grid-breakpoints, 'lg') - 1px}) {
      mask: v-bind(disruptorMaskUrl) top/auto no-repeat, linear-gradient(#fff 0 0);
      -webkit-mask: v-bind(disruptorMaskSvgUrl) top/auto no-repeat, linear-gradient(#fff 0 0);
      -webkit-mask-composite: destination-out;
      mask-composite: exclude;
      mask-size: calc(100% + 6px) auto;
      mask-position: center -3px, 0% 0%;
      background: $gradient-primary;
      position: relative;
      z-index: 3;
    }

    &__content-wrapper {
      @media (max-width: #{map_get($grid-breakpoints, 'lg') - 1px}) {
        background: none !important;
      }

      @media (min-width: map_get($grid-breakpoints, 'lg')) {
        position: absolute;
        bottom: 60px;
        width: 267px;
        height: 267px;
        border-radius: 999999px;
        right: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        align-content: center;
        transform-origin: center;
        transform: rotate(15deg);
        z-index: 5;
      }

      > div {
        flex: 1 0 100%;
        align-self: center;

        font-size: .875rem;
        @include font-weight-fix;
        color: #FFFFFF;
        padding-bottom: 10.25vw;
        padding-right: 5vw;
        text-align: left;

        @media (min-width: map_get($grid-breakpoints, 'lg')) {
          padding: var(--app-disruptor-padding-y) var(--app-disruptor-padding-x);
          text-align: center;
        }
      }
    }
  }
}
</style>
